import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Trash2, Download, Upload } from 'lucide-react'
import { useAgreementDocumentsManager } from './useAgreementDocumentsManager'

interface DocumentsCardProps {
  agreementId: string
}

export const DocumentsCard = ({ agreementId }: DocumentsCardProps) => {
  const {
    documents,
    isLoadingDocuments,
    isUploading,
    selectedFile,
    handleFileChange,
    handleUpload,
    handleDeleteDocument,
    handleDownloadDocument,
  } = useAgreementDocumentsManager(agreementId)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documentos del contrato</CardTitle>
        <CardDescription>Documentos adjuntos al contrato</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Label htmlFor="document-upload" className="sr-only">
              Subir documento
            </Label>
            <Input
              accept=".pdf,.doc,.docx"
              id="document-upload"
              type="file"
              onChange={handleFileChange}
              disabled={isUploading}
              className="cursor-pointer"
              lang="es"
              aria-label="Seleccionar archivo"
            />
          </div>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            variant="default"
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Subiendo...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Subir
              </>
            )}
          </Button>
        </div>

        {isLoadingDocuments ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : documents && documents.length > 0 ? (
          <div className="border rounded-md divide-y">
            {documents.map((doc) => (
              <div key={doc.id} className="grid grid-cols-[1fr,auto,auto] items-center p-3">
                <div className="flex items-center space-x-2 break-all">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{doc.fileName || doc.id}</span>
                </div>
                <div className="flex items-center justify-end gap-3 px-3">
                  <IconButton variant="default" title="Descargar documento"
                    onClick={() => handleDownloadDocument(doc.id, doc.fileName || doc.id)}>
                    <Download className="h-4 w-4" />
                    <span className="sr-only">Descargar</span>
                  </IconButton>
                  <IconButton variant="danger" title="Eliminar documento"
                    onClick={() => handleDeleteDocument(doc.id)}>
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Eliminar</span>
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 text-muted-foreground flex items-center justify-center gap-3">
            <FileText className="h-6 w-6 opacity-40 flex-shrink-0" />
            <p>No hay documentos adjuntos</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
